import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import moment, { Moment } from "moment";
import { DateValidationError } from "@mui/x-date-pickers";
import { useSnackbar } from '../../../../context/snackbar-context';
import request from "../../../../util/SuperagentUtils";
import { adminBookingsReportEndpoints } from "../../../../requests/api-endpoints";
import { ReportDialogProps } from "../../../../interfaces";
import { AdminBookingReportVO } from "../../../../types/model/valueObject/booking/AdminBookingReportVO";
import {
  ManualOverageInvoiceEligibilityVO,
  ManualOverageInvoiceInputVO,
  ManualOverageInvoicePreviewVO,
} from "../../../../types/model/valueObject/booking/ManualOverageInvoiceVO";
import { getErrorMessage } from "../../../../util/NetworkErrorUtil";
import { formatCurrencyValue, parseCurrencyValue } from "../../../../util/PaymentUtils";
import DatePicker from "../../../common/inputs/DatePicker/DatePicker";
import CostBreakdown from "../../../deprecatedUikit/CostBreakdown/CostBreakdown";

const ISO_DATE = 'YYYY-MM-DD';
const PREVIEW_DEBOUNCE_MS = 400;
// Overage invoices for bookings that ended longer ago than this are allowed, but the admin is warned.
const OLD_BOOKING_WARNING_DAYS = 60;

const cannedMessages = {
  eligibilityError: 'Unable to check whether an overage invoice can be created for this booking.',
  previewError: 'Unable to calculate the overage invoice amounts.',
  genericError: 'Unable to create the overage invoice. Please contact support.',
  success: (orderNumber: string, invoiceNumber?: number) => invoiceNumber
    ? `Created overage invoice #${invoiceNumber} for booking ${orderNumber}.`
    : `Created an overage invoice for booking ${orderNumber}.`,
};

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DrawerBody = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2, 3),
}));

const DrawerFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey["100"],
}));

const BookingDetail = ({ label, value }: { label: string, value?: string | number }) => (
  <Grid item xs={6}>
    <Typography variant="caption" color="textSecondary">{label}</Typography>
    <Typography variant="body2">{value || '-'}</Typography>
  </Grid>
);

const CreateOverageInvoiceDrawer = ({
  item,
  open,
  onClose,
}: ReportDialogProps<AdminBookingReportVO>) => {
  const { snackbarShowMessage } = useSnackbar();

  const [eligibility, setEligibility] = useState<ManualOverageInvoiceEligibilityVO | undefined>(undefined);
  const [eligibilityError, setEligibilityError] = useState<string | undefined>(undefined);
  const [loadingEligibility, setLoadingEligibility] = useState(false);

  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  // The DatePicker skips onChange for invalid entries, so track errors to block a stale preview/submit.
  const [startDateError, setStartDateError] = useState<DateValidationError>(null);
  const [endDateError, setEndDateError] = useState<DateValidationError>(null);
  const [numberOfSpaces, setNumberOfSpaces] = useState('1');
  const [buyerAmountPerSpace, setBuyerAmountPerSpace] = useState(0);
  const [supplierAmountPerSpace, setSupplierAmountPerSpace] = useState(0);
  const [notes, setNotes] = useState('');

  const [preview, setPreview] = useState<ManualOverageInvoicePreviewVO | undefined>(undefined);
  const [previewError, setPreviewError] = useState<string | undefined>(undefined);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    let cancelled = false;
    setEligibility(undefined);
    setEligibilityError(undefined);
    setStartDate(null);
    setEndDate(null);
    setStartDateError(null);
    setEndDateError(null);
    setNumberOfSpaces('1');
    setBuyerAmountPerSpace(0);
    setSupplierAmountPerSpace(0);
    setNotes('');
    setLoadingEligibility(true);
    request.get(adminBookingsReportEndpoints.actions.manualOverageInvoiceEligibility(item.id))
      .then((res) => { if (!cancelled) setEligibility(res.body); })
      .catch((err) => { if (!cancelled) setEligibilityError(getErrorMessage(err, cannedMessages.eligibilityError)); })
      .finally(() => { if (!cancelled) setLoadingEligibility(false); });

    return () => { cancelled = true; };
  }, [open, item]);

  const spacesValid = /^\d+$/.test(numberOfSpaces) && Number.isSafeInteger(Number(numberOfSpaces)) && Number(numberOfSpaces) >= 1;
  const spaces = spacesValid ? Number(numberOfSpaces) : 0;
  const endBeforeStart = Boolean(startDate && endDate && endDate.isBefore(startDate, 'day'));
  // parseCurrencyValue passes non-numeric text through unchanged, so check the parsed values here.
  const buyerAmountValid = Number.isSafeInteger(buyerAmountPerSpace) && buyerAmountPerSpace > 0;
  const supplierAmountValid = Number.isSafeInteger(supplierAmountPerSpace) && supplierAmountPerSpace >= 0;
  const supplierAboveBuyer = buyerAmountValid && supplierAmountValid && supplierAmountPerSpace > buyerAmountPerSpace;
  const hasDateError = Boolean(startDateError || endDateError);

  const amountsInput = useMemo<ManualOverageInvoiceInputVO | undefined>(() => {
    if (!startDate || !endDate || hasDateError || endBeforeStart || !spacesValid
      || !buyerAmountValid || !supplierAmountValid || supplierAboveBuyer) {
      return undefined;
    }
    return {
      serviceStartDate: startDate.format(ISO_DATE),
      serviceEndDate: endDate.format(ISO_DATE),
      numberOfSpaces: spaces,
      buyerAmountPerSpace,
      supplierAmountPerSpace,
    };
  }, [startDate, endDate, hasDateError, endBeforeStart, spacesValid, spaces, buyerAmountValid, supplierAmountValid,
    buyerAmountPerSpace, supplierAmountPerSpace, supplierAboveBuyer]);

  useEffect(() => {
    setPreview(undefined);
    setPreviewError(undefined);
    if (!open || !item || !eligibility?.eligible || !amountsInput) return;

    let cancelled = false;
    setLoadingPreview(true);
    const timer = setTimeout(() => {
      request.post(adminBookingsReportEndpoints.actions.previewManualOverageInvoice(item.id))
        .send(amountsInput)
        .then((res) => { if (!cancelled) setPreview(res.body); })
        .catch((err) => { if (!cancelled) setPreviewError(getErrorMessage(err, cannedMessages.previewError)); })
        .finally(() => { if (!cancelled) setLoadingPreview(false); });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      setLoadingPreview(false);
    };
  }, [open, item, eligibility, amountsInput]);

  // Completing a request after dismissal would close whichever dialog the admin opened next.
  const handleClose = () => {
    if (submitting) return;
    onClose({ item });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!item || !amountsInput) return;
    setSubmitting(true);
    request.post(adminBookingsReportEndpoints.actions.createManualOverageInvoice(item.id))
      .send({ ...amountsInput, notes: notes.trim() || undefined })
      .then((res) => {
        snackbarShowMessage(cannedMessages.success(item.orderNumber, res.body?.transactionNumber), 'success');
        onClose({ item, action: 'SUCCESS' });
      })
      .catch((err) => snackbarShowMessage(getErrorMessage(err, cannedMessages.genericError), 'error'))
      .finally(() => setSubmitting(false));
  };

  const minServiceDate = eligibility?.minServiceDate ? moment(eligibility.minServiceDate, ISO_DATE) : undefined;
  const maxServiceDate = eligibility?.maxServiceDate ? moment(eligibility.maxServiceDate, ISO_DATE) : undefined;
  const endedLongAgo = Boolean(maxServiceDate
    && maxServiceDate.isBefore(moment().startOf('day').subtract(OLD_BOOKING_WARNING_DAYS, 'days'), 'day'));
  const securSpaceFeePercentage = buyerAmountValid && supplierAmountValid
    ? `${((buyerAmountPerSpace - supplierAmountPerSpace) / buyerAmountPerSpace * 100).toFixed(2)}%`
    : '-';
  const canSubmit = Boolean(eligibility?.eligible && amountsInput && preview && !previewError && !loadingPreview && !submitting);
  const formId = 'ss-create-overage-invoice-form';
  const titleId = 'ss-create-overage-invoice-title';

  return (
    <Drawer
      anchor="right"
      open={open && Boolean(item)}
      onClose={handleClose}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': titleId,
        sx: { width: { xs: '100%', sm: 560 }, maxWidth: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      <DrawerHeader>
        <Typography id={titleId} variant="h6" component="h2">Create Overage Invoice</Typography>
        <IconButton aria-label="Close" onClick={handleClose} edge="end" disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DrawerHeader>
      <DrawerBody>
        <Grid container spacing={1} mb={2}>
          <BookingDetail label="Booking" value={item?.orderNumber} />
          <BookingDetail label="Service" value={item?.assetType} />
          <BookingDetail label="Customer" value={item?.buyerCompanyName} />
          <BookingDetail label="Supplier" value={item?.supplierCompanyName} />
          <BookingDetail label="Location" value={item?.locationName} />
          <BookingDetail label="Booking Overage Rate (Reference)" value={item?.overageRate} />
        </Grid>

        {loadingEligibility && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loadingEligibility && eligibilityError && <Alert severity="error">{eligibilityError}</Alert>}
        {!loadingEligibility && eligibility && !eligibility.eligible && (
          <Alert severity="warning">An overage invoice cannot be created for this booking: {eligibility.reason}</Alert>
        )}
        {!loadingEligibility && eligibility?.eligible && endedLongAgo && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This booking ended more than {OLD_BOOKING_WARNING_DAYS} days ago.
          </Alert>
        )}

        {!loadingEligibility && eligibility?.eligible && (
          <Box component="form" id={formId} onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  name="serviceStartDate"
                  label="Service Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  onError={setStartDateError}
                  minDate={minServiceDate}
                  maxDate={maxServiceDate}
                  slotProps={{
                    textField: {
                      variant: 'standard',
                      error: Boolean(startDateError),
                      helperText: startDateError ? 'Enter a valid date within the booking dates' : undefined,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  name="serviceEndDate"
                  label="Service End Date"
                  value={endDate}
                  onChange={setEndDate}
                  onError={setEndDateError}
                  minDate={startDate ?? minServiceDate}
                  maxDate={maxServiceDate}
                  slotProps={{
                    textField: {
                      variant: 'standard',
                      error: endBeforeStart || Boolean(endDateError),
                      helperText: endBeforeStart
                        ? 'Must be on or after the start date'
                        : endDateError ? 'Enter a valid date within the booking dates' : undefined,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="ss-overage-invoice-number-of-spaces"
                  label="Spaces"
                  name="numberOfSpaces"
                  type="number"
                  value={numberOfSpaces}
                  onChange={(e) => setNumberOfSpaces(e.target.value)}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  inputProps={{ inputMode: 'numeric', min: 1, step: 1 }}
                  error={numberOfSpaces !== '' && !spacesValid}
                  helperText={numberOfSpaces !== '' && !spacesValid ? 'Enter a whole number of at least one space' : undefined}
                  variant="standard"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="ss-overage-invoice-securspace-fee"
                  label="SecurSpace Fee % (Read-Only)"
                  value={securSpaceFeePercentage}
                  inputProps={{ readOnly: true }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="ss-overage-invoice-buyer-amount-per-space"
                  label="Customer Amount Per Space"
                  name="buyerAmountPerSpace"
                  value={formatCurrencyValue(buyerAmountPerSpace, true)}
                  onChange={(e) => setBuyerAmountPerSpace(parseCurrencyValue(e.target.value))}
                  placeholder="e.g. $50.00"
                  error={buyerAmountPerSpace !== 0 && !buyerAmountValid}
                  helperText={buyerAmountPerSpace !== 0 && !buyerAmountValid
                    ? 'Enter a dollar amount greater than $0.00'
                    : 'Negotiated amount per space for the whole service period.'}
                  inputProps={{ inputMode: 'numeric' }}
                  variant="standard"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="ss-overage-invoice-supplier-amount-per-space"
                  label="Supplier Amount Per Space"
                  name="supplierAmountPerSpace"
                  value={formatCurrencyValue(supplierAmountPerSpace, true)}
                  onChange={(e) => setSupplierAmountPerSpace(parseCurrencyValue(e.target.value))}
                  placeholder="e.g. $40.00"
                  error={!supplierAmountValid || supplierAboveBuyer}
                  helperText={!supplierAmountValid
                    ? 'Enter a dollar amount of $0.00 or more'
                    : supplierAboveBuyer ? 'Cannot be more than the customer amount' : undefined}
                  inputProps={{ inputMode: 'numeric' }}
                  variant="standard"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="ss-overage-invoice-customer-amount"
                  label="Customer Amount (Read-Only)"
                  value={formatCurrencyValue(buyerAmountValid ? buyerAmountPerSpace * spaces : 0, true)}
                  helperText="Subtotal charged to the customer before taxes and fees."
                  inputProps={{ readOnly: true }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="ss-overage-invoice-supplier-amount"
                  label="Supplier Amount (Read-Only)"
                  value={formatCurrencyValue(supplierAmountValid ? supplierAmountPerSpace * spaces : 0, true)}
                  inputProps={{ readOnly: true }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="ss-overage-invoice-notes"
                  label="Notes (Optional)"
                  name="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  inputProps={{ maxLength: 500 }}
                  variant="standard"
                  multiline
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                {previewError && <Alert severity="error">{previewError}</Alert>}
                {!previewError && (
                  <>
                    <Typography variant="h6" component="h3">Charge Breakdown</Typography>
                    <CostBreakdown
                      loading={loadingPreview}
                      items={[
                        { label: 'Supplier Payout Amount:', value: preview?.supplierTransactionAmount ?? 0 },
                        { label: 'SecurSpace Fee:', value: preview?.securspaceFeeAmount ?? 0 },
                        { label: 'Tax Amount:', value: preview?.transactionTaxAmount ?? 0 },
                        ...(preview?.paymentProcessorFeeAmount
                          ? [{ label: 'Payment Processor Fee:', value: preview.paymentProcessorFeeAmount }]
                          : []),
                      ]}
                      total={{ label: 'Total Customer Charge:', value: preview?.buyerTransactionAmount ?? 0 }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </DrawerBody>
      <DrawerFooter>
        <Button color="primary" onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button color="primary" variant="contained" type="submit" form={formId} disabled={!canSubmit}>
          Create Invoice
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

export { CreateOverageInvoiceDrawer };
